import type {Issue} from "../entities/issue.entity";
import {IssueHelper} from "./issue.helper";

/** Structural shape both `LabelColumnRow` (`<my-organizer-label-column>`)
 *  and `CalendarDayRow` (`<my-organizer-calendar-day>`) already satisfy —
 *  kept minimal (just what this helper actually needs) rather than importing
 *  either component's own row type, to avoid a component-to-component
 *  dependency for a domain-level helper. */
export interface IssueRowPair {
    issue: Issue;
    row: {uid: string};
}

export interface DiffDoneRowsResult<T extends IssueRowPair> {
    /** The next `_rows`/`_rowsByDate` value: every still-relevant fresh row,
     *  PLUS every row that just now went DONE (kept in, at the end, so the
     *  caller's `DissolveFlashController` has something to animate before it
     *  actually disappears — see `newlyDone` below). */
    rows: T[];
    /** Previously-shown rows whose Issue is DONE as of THIS fresh fetch —
     *  the caller should `DissolveFlashController.start()` each one and
     *  remove it from `rows` only once that finishes. */
    newlyDone: T[];
}

/**
 * Diffs a box/calendar cell's currently-shown rows against a fresh, RAW
 * (i.e. NOT already DONE-filtered) re-fetch of the same underlying data —
 * shared between `<my-organizer-label-column>`'s own re-fetch and
 * `<my-organizer-calendar-day>`'s parent-pushed bucket (Anke, 2026-09-09:
 * "wenn ein Ticket... auf done gesetzt wird... dann würde ich gern, dass
 * man... das Ticket noch kurz sieht und es dann mit einer Auflöseanimation
 * ... verschwindet").
 *
 * Three cases per currently-shown row:
 * - still present in `freshRawRows` and NOT done → carried over, AT ITS
 *   CURRENT POSITION (Anke, 2026-09-09, bug report: a row that dissolves
 *   must not jump to the end of the list first — see below), with the fresh
 *   pair so e.g. a title/status edit still shows up live.
 * - still present but NOW done → returned in `newlyDone` (see above) AND
 *   kept in `rows`, ALSO at its current position, for now — the row stays
 *   on screen, in place, for the dissolve animation instead of vanishing OR
 *   relocating the instant this diff runs.
 * - missing from `freshRawRows` entirely (unassigned/deleted/moved
 *   elsewhere) → dropped immediately, no animation — that's a DIFFERENT
 *   transition (already handled by this component's own window-event
 *   listeners for the "moved elsewhere" case), not the "went DONE" one this
 *   helper exists for.
 *
 * A row that's brand new (in `freshRawRows`, not `currentRows`) is appended
 * at the end — no animation for arriving, only for leaving. Implementation:
 * walk `currentRows` in its OWN order first (this is what preserves
 * position — the first version of this helper instead rebuilt `rows` from
 * `freshRawRows`'s order with `newlyDone` appended afterwards, which is
 * exactly what silently reordered a dissolving row to the end), then append
 * whatever's left in `freshRawRows` that wasn't already covered.
 *
 * `isAlreadyDissolving` (review-fund, 2026-09-09, round 4) — the caller's
 * own `DissolveFlashController.has(issueUid)`. Without this, a row that's
 * ALREADY mid-dissolve from a PRIOR call to this function would be reported
 * as `newlyDone` again on every subsequent live-refresh while it's still
 * DONE (which it stays, by definition, until actually removed) — and
 * `DissolveFlashController.start()` restarts its ENTIRE timeline
 * (`_cancel()` first) rather than leaving an in-flight one alone, visibly
 * snapping the row back to full opacity/size and re-pausing. A row this
 * predicate says is already dissolving is still carried over in `rows`
 * (unchanged) — just excluded from `newlyDone`, so the caller never
 * re-`start()`s something already running.
 */
export function diffDoneRows<T extends IssueRowPair>(
    currentRows: T[],
    freshRawRows: T[],
    isAlreadyDissolving: (issueUid: string) => boolean = () => false,
): DiffDoneRowsResult<T> {
    const freshByUid = new Map(freshRawRows.map(pair => [pair.row.uid, pair]));
    const currentUids = new Set(currentRows.map(pair => pair.row.uid));
    const newlyDone: T[] = [];

    const carriedOver = currentRows
        .map(pair => freshByUid.get(pair.row.uid))
        .filter((pair): pair is T => pair !== undefined)
        .map(pair => {
            if (IssueHelper.isDone(pair.issue) && !isAlreadyDissolving(pair.issue.uid)) {
                newlyDone.push(pair);
            }
            return pair;
        });

    const brandNew = freshRawRows.filter(pair => !currentUids.has(pair.row.uid) && !IssueHelper.isDone(pair.issue));

    return {
        rows: [...carriedOver, ...brandNew],
        newlyDone,
    };
}
